import { useCallback, useMemo } from 'react';
import {
    MeasuringPointSensorAction,
    RequestMeasuringPointSensorActionDocument,
} from '@/graphql/types';
import type { MeasuringPointWithProject } from '../types';
import type { OptionsMenuItem } from '../components/OptionsMenu';
import { createMeasuringPointMenuItems } from '../utils/menuItems/measuringPointMenuItems';
import {
    getOverviewRefetchQueriesForMeasuringPoint,
    runMutationWithRefresh,
} from '../utils/menuItems/menuItemActions';
import { usePastSensors } from '../graphql/usePastSensors';
import { useIsArchivedTab } from './useOverviewTabState';

interface ProjectMeasuringPointRowInteractions {
    menuItems: OptionsMenuItem[];
    calibrationSkeletonCount: number;
    onOptionsMenuOpenChange: (open: boolean) => void;
    onStatusRestart: () => void;
}

export function useProjectMeasuringPointRowInteractions(
    measuringPoint: MeasuringPointWithProject
): ProjectMeasuringPointRowInteractions {
    const { id, projectId } = measuringPoint;
    const archived = useIsArchivedTab();
    const { pastSensors, loadPastSensors, loading } = usePastSensors(id);

    const measuringPointWithPastSensors = useMemo(
        () =>
            pastSensors
                ? {
                      ...measuringPoint,
                      pastSensors,
                  }
                : measuringPoint,
        [pastSensors, measuringPoint]
    );
    const menuItems = useMemo(
        () => createMeasuringPointMenuItems(measuringPointWithPastSensors, archived),
        [archived, measuringPointWithPastSensors]
    );

    const onOptionsMenuOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                loadPastSensors();
            }
        },
        [loadPastSensors]
    );

    const onStatusRestart = useCallback(() => {
        void runMutationWithRefresh({
            mutation: RequestMeasuringPointSensorActionDocument,
            variables: {
                id,
                action: MeasuringPointSensorAction.Restart,
            },
            successMessage: gettext('OVERVIEW_SENSOR_RESTART_SUCCESS'),
            refetchQueries: getOverviewRefetchQueriesForMeasuringPoint(projectId, archived),
        });
    }, [archived, id, projectId]);

    return {
        menuItems,
        calibrationSkeletonCount: loading ? 2 : 0,
        onOptionsMenuOpenChange,
        onStatusRestart,
    };
}
